/**
 * FormVisibilityModeSelect
 *
 * Toolbar select for the form's visibility view: default views (View All,
 * Customer, Optimizer) plus the org's custom department views for this form
 * type, each rendered with its icon.
 *
 * Reads the store through a narrow snapshot — the form's `formType` id only —
 * and writes through `formActions`, never through `useFormStore()`, whose spread
 * of `formVersion.data` would re-render this select on every keystroke in the
 * form. The form-type id is taken from the parent form doc rather than from
 * `formType.data.docId` — `persistDocument` does inject a `docId` there, but only
 * once the id from the form doc has already attached that listener, so reading
 * `form.data.formType` resolves the select one Firestore round-trip earlier.
 */

import { FC, useMemo } from 'react';
import { Select, type SelectProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { formActions, formStore } from '../../../store';
import { useVisibilityMode } from '../../../use/useVisibilityMode';
import { useOrganizationParameters } from '../../../use/useOrganizationParameters';
import { VisibilityTypeIcon } from '../../atoms/VisibilityTypeIcon';
import { SYSTEM_DEFAULT_VISIBILITY_VIEW } from '#types/System.Parameters.types';

const FormVisibilityModeSelect: FC = () => {
  const snap = useSnapshot(formStore.state);
  const visibilityMode = useVisibilityMode();
  const { getVisibilityTypesForFormType } = useOrganizationParameters();
  const formTypeId = snap.form.data?.formType;

  const visibilityTypes = useMemo(() => {
    return getVisibilityTypesForFormType(formTypeId);
  }, [getVisibilityTypesForFormType, formTypeId]);

  const visibilityMetadata = useMemo(() => {
    return new Map(visibilityTypes.map(v => [v.id, v]));
  }, [visibilityTypes]);

  const options = useMemo<SelectProps['options']>(() => {
    return visibilityTypes.map(v => ({ value: v.id, label: v.shortLabel }));
  }, [visibilityTypes]);

  const isValidMode = visibilityMetadata.has(visibilityMode);
  const effectiveMode = isValidMode ? visibilityMode : SYSTEM_DEFAULT_VISIBILITY_VIEW;
  const selected = visibilityMetadata.get(effectiveMode);

  return (
    <Select
      aria-label="Form visibility view"
      value={effectiveMode}
      onChange={formActions.setVisibilityMode}
      style={{ 
        width: 180
      }}
      styles={{ popup: { root: {
        width: 320
      }}}}
      options={options}
      optionRender={({ data }) => {
        const metadata = visibilityMetadata.get(data.value as string);
        return (
          <span>
            <VisibilityTypeIcon name={metadata?.icon} /> {metadata?.shortLabel ?? data.value}
          </span>
        );
      }}
      showSearch={{
        filterOption: (input, option) => {
          if (!option) return true;
          const value = typeof option.value === 'string' ? option.value : String(option.value ?? '');
          const metadata = visibilityMetadata.get(value);
          const label = metadata?.shortLabel ?? value;
          const searchTerm = input.toLowerCase();
          return label.toLowerCase().includes(searchTerm) || value.toLowerCase().includes(searchTerm);
        },
      }}
      labelRender={() => (
        <span style={{ pointerEvents: 'none' }}>
          {selected?.shortLabel ?? effectiveMode}
        </span>
      )}
      prefix={<VisibilityTypeIcon name={selected?.icon} />}
      suffix={<DownOutlined style={{ pointerEvents: 'none' }} />}
    />
  );
};

export default FormVisibilityModeSelect;
